import './styles/globals.css'; // Import Tailwind styles

import * as Sentry from '@sentry/react';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import startsWith from 'lodash/startsWith';
import toLower from 'lodash/toLower';
import React from 'react';
import * as ReactDOMClient from 'react-dom/client';

import { App } from './App';
import { history } from './lib/history';

// Configure Sentry for crash reporting

const SENTRY_PROJECT = 'oilytics-development-react';

// TODO: Update build script to enforce that this is set
const RELEASE_TAG = import.meta.env.VITE_RELEASE_TAG;

const isProd = import.meta.env.PROD;
const environment = isProd ? 'production' : 'development';
const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0');
const enableSentryFlag = (() => {
  const v = toLower(String(import.meta.env.VITE_ENABLE_SENTRY || ''));
  return v === '1' || v === 'true';
})();

if (isProd || enableSentryFlag) {
  Sentry.init({
    dsn: 'https://bb0052bbe6ab4ed68514dd9e41922bf4@sentry.io/1467168',
    release: isProd && RELEASE_TAG ? `${SENTRY_PROJECT}@${RELEASE_TAG}` : undefined,
    environment,
    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({ history }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    // The Amplitude SDK logs its own transport failures (ad-blockers, flaky
    // networks, retry exhaustion) via console.error, which
    // captureConsoleIntegration would otherwise forward as Sentry errors.
    ignoreErrors: [/^Amplitude Logger \[Error\]/],
    tracesSampleRate,
  });
}

// Backstop for failed dynamic imports: deploys retain previous builds'
// hashed chunks in S3 (no `s3 sync --delete`), so stale tabs normally keep
// lazy-loading fine — but if a referenced chunk is ever missing (manual
// bucket cleanup, partial deploy), Vite emits vite:preloadError; reload once
// to pick up the new index.html. The sessionStorage guard prevents a reload
// loop if the chunk is still missing after refresh.
window.addEventListener('vite:preloadError', (event) => {
  const guard = 'vite-preload-error-reloaded';
  if (sessionStorage.getItem(guard) !== window.location.href) {
    sessionStorage.setItem(guard, window.location.href);
    event.preventDefault();
    window.location.reload();
  }
});

// CRA registered a workbox service worker scoped to /assets/. The migration
// stopped shipping it, but returning users still have the old worker
// registered, pinning the multi-MB workbox precache in Cache Storage
// indefinitely (nothing updates or evicts it anymore). Unregister it and
// drop its caches.
if (isProd && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    forEach(registrations, (registration) => registration.unregister());
  });
  if (window.caches) {
    caches.keys().then((keys) => {
      forEach(
        filter(keys, (key) => startsWith(key, 'workbox-')),
        (key) => caches.delete(key)
      );
    });
  }
}

const container = document.getElementById('root');
const root = ReactDOMClient.createRoot(container);

// Use createElement instead of JSX — the HTML entry point .js file is parsed
// by esbuild before the React plugin transforms JSX
root.render(React.createElement(App));
